import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminPricingComponentService } from "@/services/admin-pricing-component.service";
import type {
  PricingComponentFilters,
  PricingComponentDetailDto,
  CreatePricingComponentRequest,
  UpdatePricingComponentRequest,
} from "@/types/pricing-component";

/**
 * Query key factory for pricing components
 */
export const pricingComponentKeys = {
  all: ["pricing-components"] as const,
  lists: () => [...pricingComponentKeys.all, "list"] as const,
  list: (filters: PricingComponentFilters) =>
    [...pricingComponentKeys.lists(), filters] as const,
  details: () => [...pricingComponentKeys.all, "detail"] as const,
  detail: (id: string) => [...pricingComponentKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated list of pricing components
 *
 * @param filters - Filter parameters for component list
 * @returns Query result with component list and pagination
 */
export function usePricingComponents(filters: PricingComponentFilters) {
  return useQuery({
    queryKey: pricingComponentKeys.list(filters),
    queryFn: () => adminPricingComponentService.getComponents(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });
}

/**
 * Hook to fetch single pricing component by ID
 *
 * @param componentId - Component ID to fetch
 * @returns Query result with component details
 */
export function usePricingComponent(componentId: string | undefined) {
  return useQuery({
    queryKey: pricingComponentKeys.detail(componentId!),
    queryFn: () => adminPricingComponentService.getComponentById(componentId!),
    enabled: !!componentId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create new pricing component
 *
 * Automatically invalidates component list queries on success.
 *
 * @returns Mutation function and state
 */
export function useCreatePricingComponent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePricingComponentRequest) =>
      adminPricingComponentService.createComponent(data),
    onSuccess: (createdComponent) => {
      // Invalidate all component list queries
      queryClient.invalidateQueries({ queryKey: pricingComponentKeys.lists() });

      // Optionally set the created component in cache
      queryClient.setQueryData(
        pricingComponentKeys.detail(createdComponent.id),
        createdComponent,
      );

      toast.success("Pricing component created successfully", {
        description: `Component "${createdComponent.name}" has been created.`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to create pricing component", {
        description: error.message || "An unexpected error occurred",
      });
    },
  });
}

/**
 * Hook to update existing pricing component
 *
 * Automatically invalidates component queries on success.
 *
 * @returns Mutation function and state
 */
export function useUpdatePricingComponent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdatePricingComponentRequest;
    }) => adminPricingComponentService.updateComponent(id, data),
    onSuccess: (updatedComponent: PricingComponentDetailDto, variables) => {
      // Optimistically update the component detail cache
      queryClient.setQueryData(
        pricingComponentKeys.detail(variables.id),
        updatedComponent,
      );

      // Invalidate all component list queries
      queryClient.invalidateQueries({ queryKey: pricingComponentKeys.lists() });

      toast.success("Pricing component updated successfully", {
        description: "The component has been updated.",
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to update pricing component", {
        description: error.message || "An unexpected error occurred",
      });
    },
  });
}

/**
 * Hook to delete pricing component
 *
 * Automatically invalidates component queries on success.
 *
 * @returns Mutation function and state
 */
export function useDeletePricingComponent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (componentId: string) =>
      adminPricingComponentService.deleteComponent(componentId),
    onSuccess: (_, componentId) => {
      // Remove the component from cache
      queryClient.removeQueries({
        queryKey: pricingComponentKeys.detail(componentId),
      });

      // Invalidate all component list queries
      queryClient.invalidateQueries({ queryKey: pricingComponentKeys.lists() });

      toast.success("Pricing component deleted successfully", {
        description: "The component has been permanently deleted.",
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to delete pricing component", {
        description: error.message || "An unexpected error occurred",
      });
    },
  });
}
